/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, OnChanges, SimpleChanges, ViewRef} from '@angular/core';
import {interpolateWarning} from './interpolate-warnings-filter';

import * as _ from 'lodash';
import {LocalNetworkProfile} from '../../../../components/session/local-network-profile';
import {bufferTemplateFlag, BufferedFlag, createBufferedFlag, deferMarkForCheck} from '../../util/template-state';

@Component({
  standalone: false,
  selector: 'validation-warnings',
  template: `
    <div *ngIf="usesFormState">
      <div *ngIf="showValidationError || showBulkError" class="alert alert-danger">
        <p *ngIf="showValidationError">Your submission had a validation error. Adjust your input according to the details below.</p>
        <p *ngIf="showBulkError">Your bulk edits had a validation error when applied to one of your selections. Adjust your edit according to the details below or change your selections.</p>
        <ul *ngIf="hasResolvedErrors">
          <li *ngFor="let error of resolvedApiErrors; trackBy: trackByApiError">{{interpolateWarning(error.apiText)}}</li>
        </ul>
      </div>
      <!-- Must be on one line with no white space to ensure :empty CSS style works -->
      <div class="alert alert-danger" *ngIf="displayPreventsAdServing.length > 0 && extra"><p>This {{ extra }} cannot deliver until the warnings are resolved.</p></div>
      <div class="alert alert-warning" *ngIf="displayPreventsAdServing.length > 0"><p *ngFor="let warning of displayPreventsAdServing; trackBy: trackByWarning">{{ interpolateWarning(warning) }}</p></div>
      <div class="alert alert-info" *ngIf="displayStandardWarnings.length > 0"><p *ngFor="let warning of displayStandardWarnings; trackBy: trackByWarning">{{ interpolateWarning(warning) }}</p></div>
    </div>
  `
})
export class ValidationWarningsComponent implements OnChanges {
  @Input() warnings: any;
  @Input() hasValidationError: boolean;
  @Input() apiErrors: Array<{ key: string; apiText: string }>;
  @Input() clientErrors: any[];
  @Input() bulkError: boolean;
  /** Supports simple form-state objects used by bulk-action modals. */
  @Input() form: any;
  @Input() skips: [string];
  @Input() extra: string;

  displayPreventsAdServing: any[] = [];
  displayStandardWarnings: any[] = [];

  private warningListsInitialized = false;
  private readonly usesFormStateFlag = createBufferedFlag();
  private readonly validationErrorFlag = createBufferedFlag();
  private readonly bulkErrorFlag = createBufferedFlag();
  private readonly resolvedErrorsFlag = createBufferedFlag();

  constructor(
    @Inject(LocalNetworkProfile) private LocalNetworkProfile,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  get usesFormState(): boolean {
    return this.readTemplateFlag(this.usesFormStateFlag, this.hasValidationError !== undefined
      || this.apiErrors !== undefined
      || this.clientErrors !== undefined
      || this.bulkError !== undefined
      || !!this.form);
  }

  get showValidationError(): boolean {
    const next = this.hasValidationError !== undefined
      ? this.hasValidationError
      : !!this.form?.validationError;
    return this.readTemplateFlag(this.validationErrorFlag, next);
  }

  get showBulkError(): boolean {
    const next = this.bulkError !== undefined ? this.bulkError : !!this.form?.bulkError;
    return this.readTemplateFlag(this.bulkErrorFlag, next);
  }

  get resolvedApiErrors(): Array<{ key: string; apiText: string }> {
    if (this.apiErrors !== undefined) {
      return this.apiErrors;
    }
    return this.form?.apiErrors || [];
  }

  get resolvedClientErrors(): any[] {
    if (this.clientErrors !== undefined) {
      return this.clientErrors;
    }
    return this.form?.clientErrors || [];
  }

  /** Use > 0 — `length || length` binds the numeric length (1→2 NG0100). */
  get hasResolvedErrors(): boolean {
    return this.readTemplateFlag(
      this.resolvedErrorsFlag,
      this.resolvedApiErrors.length > 0 || this.resolvedClientErrors.length > 0
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.warnings || changes.skips) {
      this.syncWarningLists();
    }
  }

  trackByApiError(index: number, error: {key?: string; apiText?: string}): string {
    return error?.key || error?.apiText || String(index);
  }

  trackByWarning(index: number, warning: {code?: string; message?: string}): string {
    return warning?.code || warning?.message || String(index);
  }

  interpolateWarning(warning) {
    return interpolateWarning(warning, this.LocalNetworkProfile.isPureMarketplacePlatform());
  }

  private readTemplateFlag(state: BufferedFlag, next: boolean): boolean {
    return bufferTemplateFlag(state, !!next, {
      isAlive: () => !(this.cdr as ViewRef).destroyed,
      onCommit: () => this.cdr.markForCheck()
    });
  }

  private syncWarningLists(): void {
    const nextPrevents = this.computePreventsAdServing();
    const nextStandard = this.computeStandardWarnings();
    if (!this.warningListsInitialized) {
      this.warningListsInitialized = true;
      this.displayPreventsAdServing = nextPrevents;
      this.displayStandardWarnings = nextStandard;
      return;
    }
    // Parent often swaps validationWarnings during the same CD turn as form settle.
    deferMarkForCheck(this.cdr, () => {
      this.displayPreventsAdServing = nextPrevents;
      this.displayStandardWarnings = nextStandard;
    });
  }

  private computePreventsAdServing(): any[] {
    const theSkips = this.skips;
    return _.filter(this.warnings, function(w) {
      return w.preventsAdServing && w.code !== 'validation.warning.user.segment.global.missing.team' && !_.includes(theSkips, w.code);
    });
  }

  private computeStandardWarnings(): any[] {
    const theSkips = this.skips;
    return _.filter(this.warnings, function(w) {
      return !w.preventsAdServing && w.code !== 'validation.warning.user.segment.global.missing.team' && !_.includes(theSkips, w.code);
    });
  }
}
